import {getAnalyticsCapabilitiesV2, postAnalyticsEventV2} from '../api/analytics'

/**
 * BI-UI v2 Portal client adapter（docs/behavior-insights/portal-merchant-ui/implementation.md
 * §4.2–§4.4、s0-hook-map.md）。
 *
 * 只做三件事：session/flow 生命週期、capabilities 門檻、送出與重試。業務語意（哪個
 * feature、哪個 step）由呼叫端決定；這裡不認識 search/inquiry/pricing 這些名詞。
 *
 * 失敗一律吞掉：capabilities 讀取失敗或 enabled=false → 本頁完全不送 v2（不回退 v1
 * 雙寫、不重試）；送出失敗只有 network/5xx 重試一次，4xx/429 不重試。
 */

const SCHEMA_VERSION = 2
const SESSION_STORAGE_KEY = 'os_bi_session_v2'
const SESSION_IDLE_MS = 30 * 60 * 1000
const FLOW_STORAGE_PREFIX = 'os_bi_flow_v2_'
const FLOW_TTL_MS = 24 * 60 * 60 * 1000
const EXPOSURE_STORAGE_PREFIX = 'os_bi_exposed_v2_'
const RETRY_MIN_MS = 1000
const RETRY_MAX_MS = 3000
const ACTIVE_INTERVAL_IDLE_MS = 30 * 1000
const ACTIVE_INTERVAL_FLUSH_MS = 30 * 1000

export type StepAction = 'attempt' | 'back' | 'retry' | 'client_confirmed'

export interface AnalyticsContextV2 {
    flow_id: string | null
    session_id: string
}

interface SessionRecord {
    id: string
    lastActivityAt: number
}

interface FlowRecord {
    id: string
    createdAt: number
}

interface CapabilitiesState {
    enabled: boolean
    allowedEvents: Set<string>
}

function hasWindow(): boolean {
    return typeof window !== 'undefined'
}

/** RFC4122 v4 UUID。優先用 crypto.randomUUID；沒有時用 fallback（舊瀏覽器/測試環境）。 */
export function randomUuid(): string {
    const w = window as unknown as { crypto?: { randomUUID?: () => string } }
    if (w.crypto?.randomUUID) {
        return w.crypto.randomUUID()
    }
    let seed = Date.now()
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (seed + Math.random() * 16) % 16 | 0
        seed = Math.floor(seed / 16)
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
    })
}

function readJson<T>(key: string): T | null {
    if (!hasWindow()) return null
    try {
        const raw = window.sessionStorage.getItem(key)
        return raw ? (JSON.parse(raw) as T) : null
    } catch {
        return null
    }
}

function writeJson(key: string, value: unknown): void {
    if (!hasWindow()) return
    try {
        window.sessionStorage.setItem(key, JSON.stringify(value))
    } catch {
        // storage 滿了或被封鎖:量測是附加價值,不能讓埋點拋例外波及業務流程。
    }
}

function removeKey(key: string): void {
    if (!hasWindow()) return
    try {
        window.sessionStorage.removeItem(key)
    } catch {
        // 同上,安靜忽略。
    }
}

/** viewport 寬度轉 device_class（§3.1 enum）。純函式,獨立於 window 以利測試。 */
export function deviceClassFromWidth(width: number): 'desktop' | 'mobile' | 'tablet' {
    if (width < 768) return 'mobile'
    if (width < 1024) return 'tablet'
    return 'desktop'
}

function currentDeviceClass(): string | undefined {
    if (!hasWindow()) return undefined
    const w = window as unknown as { innerWidth?: number }
    return typeof w.innerWidth === 'number' ? deviceClassFromWidth(w.innerWidth) : undefined
}

/** release_id 只接受既有 VITE_APP_VERSION,且需符合後端 ASCII≤64 的 allowlist；不符合就省略。 */
function currentReleaseId(): string | undefined {
    const v = (import.meta.env?.VITE_APP_VERSION as string | undefined) ?? undefined
    if (!v || !/^[A-Za-z0-9_.-]{1,64}$/.test(v)) return undefined
    return v
}

let capabilitiesPromise: Promise<CapabilitiesState> | null = null

/** 測試用：重置 capabilities 快取（「每頁載入讀一次」的快取在模組層級,跨測試案例要重置）。 */
export function resetAnalyticsV2Capabilities(): void {
    capabilitiesPromise = null
}

async function loadCapabilities(): Promise<CapabilitiesState> {
    if (!capabilitiesPromise) {
        capabilitiesPromise = getAnalyticsCapabilitiesV2()
            .then((res) => {
                if (!res || res.enabled !== true) {
                    return {enabled: false, allowedEvents: new Set<string>()}
                }
                return {enabled: true, allowedEvents: new Set(res.allowed_events ?? [])}
            })
            .catch(() => ({enabled: false, allowedEvents: new Set<string>()}))
    }
    return capabilitiesPromise
}

function sessionKey(): string {
    return SESSION_STORAGE_KEY
}

/**
 * 取得/延續本分頁的 session；逾 30 分鐘無互動則視為新 session,並補發一次 session_start。
 *
 * session_start 的 sendEventInternal 會自己把剛產生的 id 當 sessionIdOverride 傳下去,
 * 不能讓它再呼叫 ensureSession() 回頭重算——sessionStorage 永久壞掉(讀永遠 miss、
 * 寫永遠靜默失敗)時,每次 ensureSession() 都會判定「沒有既有 session」而再建一個新的,
 * 遞迴送 session_start 會無窮下去(實測會直接 stack overflow)。
 */
function ensureSession(): string {
    if (!hasWindow()) return ''
    const t = Date.now()
    const existing = readJson<SessionRecord>(sessionKey())
    if (existing && t - existing.lastActivityAt < SESSION_IDLE_MS) {
        existing.lastActivityAt = t
        writeJson(sessionKey(), existing)
        return existing.id
    }
    const fresh: SessionRecord = {id: randomUuid(), lastActivityAt: t}
    writeJson(sessionKey(), fresh)
    void sendEventInternal('session_start', {}, null, null, fresh.id)
    return fresh.id
}

/** 登出/換帳/換端清除 session（implementation.md §4.3）。 */
export function clearBehaviorSession(): void {
    removeKey(sessionKey())
}

function flowKey(featureId: string): string {
    return FLOW_STORAGE_PREFIX + featureId
}

/** 只讀目前 flow,不建立；過期回 null。供「可能沒有任務」的曝光類事件使用。 */
function peekFlow(featureId: string): string | null {
    const existing = readJson<FlowRecord>(flowKey(featureId))
    if (!existing) return null
    if (Date.now() - existing.createdAt >= FLOW_TTL_MS) return null
    return existing.id
}

/** 取得或建立 flow（首次任務互動時呼叫）；回傳是否為新建立。 */
function ensureFlow(featureId: string): { id: string; isNew: boolean } {
    if (!hasWindow()) return {id: '', isNew: false}
    const existing = peekFlow(featureId)
    if (existing) return {id: existing, isNew: false}
    const fresh: FlowRecord = {id: randomUuid(), createdAt: Date.now()}
    writeJson(flowKey(featureId), fresh)
    return {id: fresh.id, isNew: true}
}

/** 成功後呼叫,下次 startTask 會開新 flow（同一任務不可重複算完成）。 */
function completeFlow(featureId: string): void {
    removeKey(flowKey(featureId))
}

function exposureKey(featureId: string, entryKind: string): string {
    return EXPOSURE_STORAGE_PREFIX + featureId + ':' + entryKind
}

/** 同 session/feature/entry 只送一次；storage 壞掉時寧可重複送,不能整個不送。 */
function markExposureOnce(featureId: string, entryKind: string): boolean {
    if (!hasWindow()) return false
    const key = exposureKey(featureId, entryKind)
    try {
        if (window.sessionStorage.getItem(key)) return false
        window.sessionStorage.setItem(key, '1')
        return true
    } catch {
        return true
    }
}

function wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

/** network/5xx 重試一次(延遲 1–3 秒、同 event_id)；4xx/429 不重試。 */
async function sendWithRetry(body: Record<string, unknown>): Promise<void> {
    const result = await postAnalyticsEventV2(body)
    if (result !== 'retryable') return
    await wait(RETRY_MIN_MS + Math.random() * (RETRY_MAX_MS - RETRY_MIN_MS))
    await postAnalyticsEventV2(body)
}

async function sendEventInternal(
    eventName: string,
    payload: Record<string, unknown>,
    featureId: string | null,
    flowId: string | null,
    sessionIdOverride?: string,
): Promise<void> {
    if (!hasWindow()) return
    const caps = await loadCapabilities()
    if (!caps.enabled || !caps.allowedEvents.has(eventName)) return

    const body: Record<string, unknown> = {
        schema_version: SCHEMA_VERSION,
        event_id: randomUuid(),
        session_id: sessionIdOverride ?? ensureSession(),
        event_name: eventName,
        payload,
    }
    if (featureId) body.feature_id = featureId
    if (flowId) body.flow_id = flowId
    const releaseId = currentReleaseId()
    if (releaseId) body.release_id = releaseId
    const deviceClass = currentDeviceClass()
    if (deviceClass) body.device_class = deviceClass

    await sendWithRetry(body)
}

/**
 * task_active_interval 的純累計邏輯（可見且 30 秒內有互動才累計,§5.1）。
 * 不綁 DOM timer,呼叫端（useActiveIntervalTracker）負責排程 sample()/flush()。
 */
export function createActiveIntervalAccumulator(opts: { now?: () => number; isVisible?: () => boolean } = {}) {
    const now = opts.now ?? Date.now
    const isVisible = opts.isVisible ?? (() => (hasWindow() ? document.visibilityState === 'visible' : false))
    let lastSampleAt = now()
    // null = 尚未有過互動(不能用 0,0 是合法的時間戳,會被誤判成「有互動」)。
    let lastInteractionAt: number | null = null
    let activeMs = 0

    function sample(): void {
        const t = now()
        const elapsed = t - lastSampleAt
        if (elapsed > 0 && lastInteractionAt !== null && isVisible() && t - lastInteractionAt <= ACTIVE_INTERVAL_IDLE_MS) {
            activeMs = Math.min(30000, activeMs + elapsed)
        }
        lastSampleAt = t
    }

    return {
        recordInteraction(): void {
            lastInteractionAt = now()
        },
        sample,
        /** 取出累積毫秒數並歸零(flush 用)；0 代表這段沒有有效互動,呼叫端應跳過送出。 */
        consumeDurationMs(): number {
            sample()
            const duration = activeMs
            activeMs = 0
            lastInteractionAt = null
            return duration
        },
    }
}

export type ActiveIntervalAccumulator = ReturnType<typeof createActiveIntervalAccumulator>

export function useBehaviorFlow() {
    return {
        /** 每頁載入讀一次 capabilities；未啟用或讀取失敗 → false,呼叫端不雙寫、不重試。 */
        async isV2Enabled(): Promise<boolean> {
            if (!hasWindow()) return false
            const caps = await loadCapabilities()
            return caps.enabled
        },

        sessionId(): string {
            return ensureSession()
        },

        /** 供業務請求 body 帶 `analytics_context:{flow_id,session_id}`（§4.4）。 */
        analyticsContext(featureId: string): AnalyticsContextV2 {
            return {flow_id: peekFlow(featureId), session_id: ensureSession()}
        },

        clearSession: clearBehaviorSession,

        /** 入口 ≥50% 可見 ≥1 秒時由呼叫端觸發（IntersectionObserver 邏輯留給元件層）。 */
        trackFeatureExposed(featureId: string, entryKind: string): void {
            if (!markExposureOnce(featureId, entryKind)) return
            void sendEventInternal('feature_exposed', {entry_kind: entryKind}, featureId, peekFlow(featureId))
        },

        /** 第一次實際編輯/選擇時呼叫;同一 flow 只送一次 task_started。 */
        startTask(featureId: string, step: string, operationKind: string): void {
            const {id: flowId, isNew} = ensureFlow(featureId)
            if (!isNew) return
            void sendEventInternal('task_started', {step, operation_kind: operationKind}, featureId, flowId)
        },

        trackStep(featureId: string, step: string, action: StepAction): void {
            const {id: flowId} = ensureFlow(featureId)
            void sendEventInternal('task_step', {step, action}, featureId, flowId)
        },

        trackFailed(featureId: string, step: string, errorCategory: string, errorCode?: string): void {
            const {id: flowId} = ensureFlow(featureId)
            const payload: Record<string, unknown> = {step, error_category: errorCategory}
            if (errorCode) payload.error_code = errorCode
            void sendEventInternal('task_failed', payload, featureId, flowId)
        },

        trackHelpOpened(featureId: string, step: string, helpKind: string): void {
            const {id: flowId} = ensureFlow(featureId)
            void sendEventInternal('help_opened', {step, help_kind: helpKind}, featureId, flowId)
        },

        trackContactIntent(featureId: string, channel: string, inquiryMode: string, candidateCount?: number): void {
            const {id: flowId} = ensureFlow(featureId)
            const payload: Record<string, unknown> = {channel, inquiry_mode: inquiryMode}
            if (typeof candidateCount === 'number') payload.candidate_count = candidateCount
            void sendEventInternal('contact_intent', payload, featureId, flowId)
        },

        /** 成功後呼叫,結束本次任務的 flow；下次 startTask 會開新 flow。 */
        completeTask(featureId: string): void {
            completeFlow(featureId)
        },

        /** portal_discovery 系列:implicit feature,不帶 feature_id/flow_id。 */
        trackSearchPerformed(requestId: string, resultCount: number): void {
            void sendEventInternal('search_performed', {request_id: requestId, result_count: resultCount}, null, null)
        },
        trackSearchNoResult(requestId: string): void {
            void sendEventInternal('search_no_result', {request_id: requestId}, null, null)
        },
        trackViewSpace(spaceId: string, requestId?: string, entryKind?: string): void {
            const payload: Record<string, unknown> = {space_id: spaceId}
            if (requestId) payload.request_id = requestId
            if (entryKind) payload.entry_kind = entryKind
            void sendEventInternal('view_space', payload, null, null)
        },
        trackSelectSpace(spaceId: string, requestId?: string, entryKind?: string): void {
            const payload: Record<string, unknown> = {space_id: spaceId}
            if (requestId) payload.request_id = requestId
            if (entryKind) payload.entry_kind = entryKind
            void sendEventInternal('select_space', payload, null, null)
        },

        /** task_active_interval:每 30 秒或隱藏時 flush,呼叫端只需驅動計時器與 recordInteraction。 */
        useActiveIntervalTracker(featureId: string) {
            const acc = createActiveIntervalAccumulator()
            let timer: ReturnType<typeof setInterval> | undefined

            function flush(): void {
                const flowId = peekFlow(featureId)
                const duration = acc.consumeDurationMs()
                if (duration <= 0 || !flowId) return
                void sendEventInternal('task_active_interval', {interval_id: randomUuid(), duration_ms: duration}, featureId, flowId)
            }

            function onVisibilityChange(): void {
                if (hasWindow() && document.visibilityState === 'hidden') flush()
            }

            return {
                recordInteraction: acc.recordInteraction,
                flush,
                start(): void {
                    if (!hasWindow()) return
                    acc.recordInteraction()
                    timer = setInterval(flush, ACTIVE_INTERVAL_FLUSH_MS)
                    window.addEventListener('visibilitychange', onVisibilityChange)
                },
                stop(): void {
                    if (timer) clearInterval(timer)
                    if (hasWindow()) window.removeEventListener('visibilitychange', onVisibilityChange)
                    flush()
                },
            }
        },
    }
}
